import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SidebarNav from '../components/Sidebar';
import TopBar from '../components/topbar';
import userPlaceholder from '../assets/userPlaceholder.png';
import Loadingrevolver from '../assets/loading.gif';
import BreedsTypeMap from '../typemaps/horsebreeds';
import WagonTypeMap from '../typemaps/wagons';
import { GiMining, GiHammerNails , GiDeer, GiFishingHook } from 'react-icons/gi';
import { parseSkills } from '../utils/skills';
import '../App.css';

const CharacterPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('stables');
  const [imageError, setImageError] = useState(false);
  
  // Data states
  const [horses, setHorses] = useState([]);
  const [wagons, setWagons] = useState([]);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [characterInfo, setCharacterInfo] = useState(null);
  const [isLoadingHorses, setIsLoadingHorses] = useState(true);
  const [isLoadingWagons, setIsLoadingWagons] = useState(true);
  const [isLoadingProperties, setIsLoadingProperties] = useState(true);
  const [isLoadingContacts, setIsLoadingContacts] = useState(true);
  const [isLoadingCharacter, setIsLoadingCharacter] = useState(true);
  const [error, setError] = useState(null);
  const [charName, setCharName] = useState('');
  const [charId, setCharId] = useState('');

  const imageUrl = characterInfo?.pfp || '';

  const tabs = [
    { id: 'stables', label: 'Stables' },
    { id: 'properties', label: 'Properties' },
    { id: 'skills', label: 'Skills' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'government', label: 'Government' }
  ];

  useEffect(() => {
    const tab = location.state?.tab || 'stables';
    setActiveTab(tab);
  }, [location]);

  // Fetch all character data
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const characterID = localStorage.getItem('selectedChar');
        const storedCharName = localStorage.getItem('selectedCharName');

        if (!characterID) {
          throw new Error('No Character Selected');
        }

        setCharId(characterID);

        if (storedCharName) {
          setCharName(storedCharName);
        }

        // Fetch character info
        setIsLoadingCharacter(true);
        console.log('charid: ', characterID)
        const charInfoResponse = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/character/${characterID}`);
        console.log(charInfoResponse)
        if (charInfoResponse.ok) {
          const charInfoResult = await charInfoResponse.json();
          console.log('Character Info:', charInfoResult); // Debug log
          if (charInfoResult && charInfoResult.length > 0) {
            setCharacterInfo(charInfoResult[0]);
            // Update character name from DB if available
            if (charInfoResult[0].firstname && charInfoResult[0].lastname) {
              setCharName(`${charInfoResult[0].firstname} ${charInfoResult[0].lastname}`);
            }
          }
        }
        setIsLoadingCharacter(false);

        // Fetch horses
        setIsLoadingHorses(true);
        const horsesResponse = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/getHorses/${characterID}`);
        if (horsesResponse.ok) {
          const horsesResult = await horsesResponse.json();
          setHorses(horsesResult);
        }
        setIsLoadingHorses(false);

        // Fetch wagons
        setIsLoadingWagons(true);
        const wagonsResponse = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/getVehicles/${characterID}`);
        if (wagonsResponse.ok) {
          const wagonsResult = await wagonsResponse.json();
          setWagons(wagonsResult);
        }
        setIsLoadingWagons(false);

        // Fetch contacts
        setIsLoadingContacts(true);
        const contactsResponse = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/character/${characterID}/contacts`);
        if (contactsResponse.ok) {
          const contactsResult = await contactsResponse.json();
          setContacts(contactsResult);
        }
        setIsLoadingContacts(false);

      } catch (err) {
        setError(err.message);
        console.error("Error fetching data:", err);
        setIsLoadingHorses(false);
        setIsLoadingWagons(false);
        setIsLoadingProperties(false);
        setIsLoadingContacts(false);
        setIsLoadingCharacter(false);
      }
    };

    fetchAllData();
  }, []);

  // Fetch properties separately when characterInfo is available
  useEffect(() => {
    const fetchProperties = async () => {
      if (characterInfo?.identifier) {
        try {
          setIsLoadingProperties(true);
          const propertiesResponse = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/ownedproperties/${characterInfo.identifier}`);
          if (propertiesResponse.ok) {
            const propertiesResult = await propertiesResponse.json();
            console.log('Properties:', propertiesResult); // Debug log
            setProperties(propertiesResult);
          }
          setIsLoadingProperties(false);
        } catch (err) {
          console.error("Error fetching properties:", err);
          setIsLoadingProperties(false);
        }
      }
    };

    fetchProperties();
  }, [characterInfo]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const cardStyle = {
    background: 'rgba(255, 255, 255, 0.23)',
    border: '2px solid rgba(120, 53, 15, 0.6)',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
  };

  const headingStyle = {
    color: '#ffffff',
    fontFamily: 'serif',
    fontSize: '32px',
    marginBottom: '24px',
    borderBottom: '2px solid rgba(73, 68, 68, 0.39)',
    paddingBottom: '8px'
  };

  const labelStyle = {
    color: 'rgba(254, 243, 199, 0.6)',
    fontSize: '14px',
    marginBottom: '5px',
    fontFamily: 'serif'
  };

  const valueStyle = {
    color: '#ffffff',
    fontFamily: 'serif',
    fontSize: '20px'
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Parse skills from JSON string
  const getSkills = () => {
    const parsed = parseSkills(characterInfo?.skills);
    if (!parsed) return [];

    return [
      { name: 'Mining', level: parsed.mining.level, color: '#ffffffff', icon: <GiMining /> },
      { name: 'Crafting', level: parsed.crafting.level, color: '#ffffffff', icon: <GiHammerNails  /> },
      { name: 'Hunting', level: parsed.hunting.level, color: '#ffffffff', icon: <GiDeer /> },
      { name: 'Fishing', level: parsed.fishing.level, color: '#ffffffff', icon: <GiFishingHook /> }
    ];
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'stables':
        return (
          <div>
            {/* Horses Section */}
            <h2 style={headingStyle}>Horses</h2>
            {isLoadingHorses ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <img src={Loadingrevolver} alt="loading..." style={{ width: '100px', height: '100px' }} />
              </div>
            ) : error ? (
              <p style={{ color: '#f87171', fontFamily: 'serif', fontSize: '18px' }}>Error: {error}</p>
            ) : horses.length === 0 ? (
              <p style={{ color: 'rgba(254, 243, 199, 0.7)', fontFamily: 'serif', fontSize: '18px' }}>No horses found</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px', marginBottom: '48px' }}>
                {horses.map((horse) => {
                  const date = new Date(horse.born);
                  const formattedDate = date.toISOString().split('T')[0];
                  const breedString = BreedsTypeMap[horse.model] || horse.model;

                  return (
                    <div key={horse.id} style={cardStyle}>
                      <h3 style={{ color: '#ffffff', fontFamily: 'serif', fontSize: '20px', marginBottom: '12px' }}>{horse.name}</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <p style={{ color: 'rgba(254, 243, 199, 0.8)', fontSize: '14px', margin: 0 }}>
                          <span style={{ color: '#f59e0b' }}>•</span> <strong>Gender:</strong> {horse.gender}
                        </p>
                        <p style={{ color: 'rgba(254, 243, 199, 0.8)', fontSize: '14px', margin: 0 }}>
                          <span style={{ color: '#f59e0b' }}>•</span> <strong>Breed:</strong> {breedString}
                        </p>
                        <p style={{ color: 'rgba(254, 243, 199, 0.8)', fontSize: '14px', margin: 0 }}>
                          <span style={{ color: '#f59e0b' }}>•</span> <strong>Born:</strong> {formattedDate}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Wagons Section */}
            <h2 style={headingStyle}>Wagons</h2>
            {isLoadingWagons ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <img src={Loadingrevolver} alt="loading..." style={{ width: '100px', height: '100px' }} />
              </div>
            ) : error ? (
              <p style={{ color: '#f87171', fontFamily: 'serif', fontSize: '18px' }}>Error: {error}</p>
            ) : wagons.length === 0 ? (
              <p style={{ color: 'rgba(254, 243, 199, 0.7)', fontFamily: 'serif', fontSize: '18px' }}>No wagons found</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
                {wagons.map((wagon) => {
                  const typeString = WagonTypeMap[wagon.model] || wagon.model;

                  return (
                    <div key={wagon.id} style={cardStyle}>
                      <h3 style={{ color: '#ffffff', fontFamily: 'serif', fontSize: '20px', marginBottom: '12px' }}>{wagon.name}</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <p style={{ color: 'rgba(254, 243, 199, 0.8)', fontSize: '14px', margin: 0 }}>
                          <span style={{ color: '#f59e0b' }}>•</span> <strong>Type:</strong> {typeString}
                        </p>
                        <p style={{ color: 'rgba(254, 243, 199, 0.8)', fontSize: '14px', margin: 0 }}>
                          <span style={{ color: '#f59e0b' }}>•</span> <strong>Condition:</strong> {wagon.condition}%
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      
      case 'properties':
        return (
          <div>
            <h2 style={headingStyle}>Properties</h2>
            {isLoadingProperties ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <img src={Loadingrevolver} alt="loading..." style={{ width: '100px', height: '100px' }} />
              </div>
            ) : error ? (
              <p style={{ color: '#f87171', fontFamily: 'serif', fontSize: '18px' }}>Error: {error}</p>
            ) : properties.length === 0 ? (
              <p style={{ color: 'rgba(254, 243, 199, 0.7)', fontFamily: 'serif', fontSize: '18px' }}>No properties found</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '24px' }}>
                {properties.map((property) => (
                  <div key={property.id} style={cardStyle}>
                    <h3 style={{ color: '#ffffff', fontFamily: 'serif', fontSize: '20px', marginBottom: '4px' }}>{property.common_name}</h3>
                    <p style={{ color: 'rgba(254, 243, 199, 0.7)', fontSize: '14px', fontStyle: 'italic', marginBottom: '12px' }}>Property</p>
                    <p style={{ color: 'rgba(254, 243, 199, 0.8)', fontSize: '14px' }}>
                      <span style={{ color: '#f59e0b' }}>•</span> Tax Ledger: {property.taxledger}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      
      case 'skills':
        const skills = getSkills();
        return (
            <div>
            <h2 style={headingStyle}>Skills</h2>
            {isLoadingCharacter ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                <img src={Loadingrevolver} alt="loading..." style={{ width: '100px', height: '100px' }} />
                </div>
            ) : skills.length === 0 ? (
                <p style={{ color: 'rgba(254, 243, 199, 0.7)', fontFamily: 'serif', fontSize: '18px' }}>No skills data available</p>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
                {skills.map((skill, index) => (
                    <div key={index} style={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ 
                        color: '#ffffff', 
                        fontFamily: 'serif', 
                        fontSize: '24px', 
                        margin: 0
                        }}>
                        {skill.name}
                        </h3>
                        <div style={{ fontSize: '32px', color: skill.color }}>
                        {skill.icon}
                        </div>
                    </div>
                    <p style={{ 
                        color: skill.color, 
                        fontFamily: 'serif', 
                        fontSize: '48px', 
                        margin: 0,
                        fontWeight: 'bold'
                    }}>
                        {skill.level}
                    </p>
                    </div>
                ))}
                </div>
            )}
            </div>
        );
      
      case 'contacts':
        return (
          <div>
            <h2 style={headingStyle}>Contacts</h2>
            {isLoadingContacts ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <img src={Loadingrevolver} alt="loading..." style={{ width: '100px', height: '100px' }} />
              </div>
            ) : error ? (
              <p style={{ color: '#f87171', fontFamily: 'serif', fontSize: '18px' }}>Error: {error}</p>
            ) : contacts.length === 0 ? (
              <p style={{ color: 'rgba(254, 243, 199, 0.7)', fontFamily: 'serif', fontSize: '18px' }}>No contacts found</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
                {contacts.map((contact) => (
                  <div key={contact.id} style={cardStyle}>
                    <h3 style={{ color: '#ffffff', fontFamily: 'serif', fontSize: '20px', marginBottom: '12px' }}>{contact.contact_name}</h3>
                    <p style={{ color: 'rgba(254, 243, 199, 0.8)', fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#f59e0b' }}>•</span> <strong>Mailbox ID:</strong> {contact.contact_mailboxid}
                    </p>
                    {contact.note && (
                      <p style={{ color: 'rgba(254, 243, 199, 0.8)', fontSize: '14px', margin: 0 }}>
                        <span style={{ color: '#f59e0b' }}>•</span> <strong>Note:</strong> {contact.note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      
      case 'government':
        return (
          <div>
            <h2 style={headingStyle}>Government Records</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={cardStyle}>
                <h3 style={{ color: '#ffffff', fontFamily: 'serif', fontSize: '20px', marginBottom: '16px', borderBottom: '1px solid rgba(255, 252, 250, 0.5)', paddingBottom: '8px' }}>Citizenship Papers</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <div>
                    <p style={labelStyle}>Status</p>
                    <p style={{ ...valueStyle, color: '#4ade80' }}>Valid Citizen</p>
                  </div>
                  <div>
                    <p style={labelStyle}>Character ID</p>
                    <p style={valueStyle}>{charId}</p>
                  </div>
                  <div>
                    <p style={labelStyle}>Job</p>
                    <p style={valueStyle}>{characterInfo?.job || 'Unemployed'}</p>
                  </div>
                  <div>
                    <p style={labelStyle}>Gender</p>
                    <p style={valueStyle}>{characterInfo?.gender || 'Unknown'}</p>
                  </div>
                </div>
              </div>
              <div style={cardStyle}>
                <h3 style={{ color: '#ffffff', fontFamily: 'serif', fontSize: '20px', marginBottom: '16px', borderBottom: '1px solid rgba(120, 53, 15, 0.5)', paddingBottom: '8px' }}>Financial Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <div>
                    <p style={labelStyle}>Current Money</p>
                    <p style={{ ...valueStyle, color: '#4ade80', fontSize: '32px' }}>${characterInfo?.money?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>
              <div style={cardStyle}>
                <h3 style={{ color: '#ffffff', fontFamily: 'serif', fontSize: '20px', marginBottom: '16px', borderBottom: '1px solid rgba(120, 53, 15, 0.5)', paddingBottom: '8px' }}>Character Description</h3>
                <p style={{ color: 'rgba(254, 243, 199, 0.8)', fontSize: '16px', lineHeight: '1.6' }}>
                  {characterInfo?.character_desc || 'No description available'}
                </p>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="content">
      <div className="topNav">
        <TopBar />
      </div>
      <div className="sidebar-container">
        <SidebarNav />
      </div>
      <div className="test" style={{ display: 'flex' }}>
        {/* Character Profile Sidebar */}
        <div style={{ 
          width: '320px', 
          background: 'rgba(28, 25, 23, 0.6)', 
          borderRight: '4px solid rgba(73, 68, 68, 0.39)', 
          padding: '24px',
          minHeight: '100vh'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {isLoadingCharacter ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <img src={Loadingrevolver} alt="loading..." style={{ width: '100px', height: '100px' }} />
              </div>
            ) : (
              <>
                <div style={{ 
                  width: '176px', 
                  height: '176px', 
                  background: 'rgba(255, 255, 255, 0.23)', 
                  border: '4px solid rgba(120, 53, 15, 0.8)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginBottom: '24px',
                  overflow: 'hidden'
                }}>
                  <img
                    src={!imageUrl || imageError ? userPlaceholder : imageUrl}
                    alt="Character"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setImageError(true)}
                  />
                </div>
                <h2 style={{ color: '#FFFFFF', fontSize: '32px', fontFamily: 'serif', marginBottom: '32px', textAlign: 'center' }}>
                  {charName || 'Loading...'}
                </h2>
                
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column'}}>
                  <p style={labelStyle}>Character ID</p>
                  <p style={valueStyle}>{charId || 'N/A'}</p>
                  
                  <p style={labelStyle}>Time Played</p>
                  <p style={valueStyle}>{characterInfo?.hours || 0} Hours</p>
                  
                  <p style={labelStyle}>Last Login</p>
                  <p style={valueStyle}>{characterInfo?.LastLogin ? formatDate(characterInfo.LastLogin) : 'Unknown'}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div style={{ flex: 1 }}>
          {/* Tabs */}
          <div style={{ background: 'rgba(28, 25, 23, 0.4)', borderBottom: '4px solid rgba(73, 68, 68, 0.39)' }}>
            <div style={{ display: 'flex' }}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  style={{
                    padding: '16px 32px',
                    fontFamily: 'serif',
                    fontSize: '18px',
                    background: activeTab === tab.id ? 'rgba(120, 53, 15, 0.6)' : 'transparent',
                    color: activeTab === tab.id ? '#ffffff' : 'rgba(254, 243, 199, 0.6)',
                    border: 'none',
                    borderBottom: activeTab === tab.id ? '4px solid #d97706' : '4px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.color = '#ffffff';
                      e.target.style.background = 'rgba(28, 25, 23, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.color = 'rgba(254, 243, 199, 0.6)';
                      e.target.style.background = 'transparent';
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div style={{ padding: '32px', background: 'rgba(28, 25, 23, 0.2)' }}>
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterPage;